import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Confetti,
  CheckCircle,
  CircleDashed,
  CircleNotch,
  Baby,
  ChalkboardTeacher,
  ShieldCheck,
  ArrowRight,
} from "@phosphor-icons/react";
import { loadRehearsalSignals, rehearsalScore } from "@/lib/launchRehearsal";

/**
 * /admin/launch-rehearsal
 *
 * A calm, read-only checklist for the ~30-minute walk-through admins
 * usually want to do before flipping the product on for real families.
 *
 * Three persona sections (Parent / Teacher / Admin), each with a small
 * number of concrete steps that map to a real flow in the app. Where a
 * data signal exists, the row shows a live indicator that flips green
 * the moment the underlying database reflects the action — no manual
 * tick boxes, no extra storage to maintain.
 *
 * Steps without a data signal are shown as "manual" and ask the admin
 * to confirm visually during the walk-through. We deliberately don't
 * fake automation we can't deliver.
 */
export default function AdminLaunchRehearsal() {
  const [signals, setSignals] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const out = await loadRehearsalSignals();
      setSignals(out);
    } catch {
      setSignals(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const score = rehearsalScore(signals);

  const personas = [
    {
      key: "parent",
      label: "Parent",
      icon: Baby,
      blurb:
        "Sign in as a real test parent on a phone, then confirm the calm side of the experience.",
      steps: [
        {
          key: "sign-in",
          label: "Sign in on a phone",
          detail: "Use Safari (iPhone) or Chrome (Android) and the test parent account. The home screen should greet them by first name.",
          state: "manual",
        },
        {
          key: "install",
          label: "Add to home screen / install",
          detail: "Either via Share → Add to Home Screen on iOS, or the install prompt on Android.",
          state: "manual",
        },
        {
          key: "push",
          label: "Turn on message notifications",
          detail: "Tap Enable on the onboarding strip — the system widget should reflect the new subscription.",
          state: signals?.parent?.push_active ? "ok" : "pending",
          live: true,
        },
        {
          key: "say-hi",
          label: "Send the first message",
          detail: "Open Messages and say hi. The thread should send instantly.",
          state: signals?.parent?.message_sent ? "ok" : "pending",
          live: true,
        },
        {
          key: "view-photo",
          label: "Open a child photo",
          detail: "Confirm the lightbox loads quickly and the report flag is visible.",
          state: "manual",
        },
        {
          key: "inquiry-funnel",
          label: "Inquiry funnel is reachable",
          detail: "At least one enrolment inquiry should exist (your live test counts here).",
          state: signals?.parent?.has_inquiry ? "ok" : "pending",
          live: true,
        },
      ],
    },
    {
      key: "teacher",
      label: "Teacher",
      icon: ChalkboardTeacher,
      blurb:
        "Sign in as a test teacher on a phone and walk one classroom through a normal morning.",
      steps: [
        {
          key: "sign-in",
          label: "Sign in on a phone",
          detail: "Confirm the roster loads and the bottom nav matches the teacher persona.",
          state: "manual",
        },
        {
          key: "push",
          label: "Enable message notifications",
          detail: "From settings or the install nudge — admins can ask teachers to enable in person.",
          state: signals?.teacher?.push_active ? "ok" : "pending",
          live: true,
        },
        {
          key: "attendance",
          label: "Mark attendance for the whole roster",
          detail: "Use the new filter chips and undo flow. Mark a child Late and undo the change.",
          state: "manual",
        },
        {
          key: "daily-report",
          label: "Log a daily report today",
          detail: "Meal, nap, mood — full flow. Should appear on the parent home within seconds.",
          state: signals?.teacher?.daily_report_today ? "ok" : "pending",
          live: true,
        },
        {
          key: "photo",
          label: "Upload one photo today",
          detail: "Pick a phone photo. Watch the upload-time downscale + caption flow.",
          state: signals?.teacher?.photo_uploaded_today ? "ok" : "pending",
          live: true,
        },
        {
          key: "message-back",
          label: "Reply to the parent's message",
          detail: "Confirm typing indicator and read receipts behave naturally.",
          state: "manual",
        },
      ],
    },
    {
      key: "admin",
      label: "Admin",
      icon: ShieldCheck,
      blurb:
        "Then walk yourself through the operational surface. Mostly visual — the system widget already covers most signals.",
      steps: [
        {
          key: "system-page",
          label: "Open /admin/system",
          detail: "Confirm the four panels load and the Production checklist looks accurate.",
          state: "manual",
          link: "/admin/system",
        },
        {
          key: "public-site",
          label: "Public site has a published page",
          detail: "Visit /site/<your-slug> on a phone and walk the marketing surface.",
          state: signals?.admin?.public_page_live ? "ok" : "pending",
          live: true,
        },
        {
          key: "email-sending",
          label: "Email channel is producing successes",
          detail: "Once Resend is set up in Railway, at least one notification should log status=sent.",
          state: signals?.admin?.email_sending ? "ok" : "pending",
          live: true,
        },
        {
          key: "moderation",
          label: "Photo moderation queue is clear",
          detail: "Or you've reviewed every flagged item.",
          state: signals?.admin?.moderation_clear ? "ok" : "warn",
          live: true,
        },
        {
          key: "billing",
          label: "Open one family invoice",
          detail: "Verify the receipt PDF, the autopay chip, and the payment-method picker render.",
          state: "manual",
        },
        {
          key: "inquiry-reply",
          label: "Send one quick-reply from the inquiry inbox",
          detail: "Use the new templates on /admin/inquiries to confirm mailto: opens correctly on your machine.",
          state: "manual",
          link: "/admin/inquiries",
        },
      ],
    },
  ];

  return (
    <div data-testid="admin-launch-rehearsal" className="space-y-6">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-neutral-500">
            Operations
          </p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900 sm:text-4xl">
            Launch rehearsal
          </h1>
          <p className="mt-2 max-w-2xl text-neutral-600">
            A calm, ~30-minute walk-through to confirm the product feels great
            on a real phone before the first families arrive. Indicators flip
            green automatically as you complete each action.
          </p>
        </div>
        <button
          type="button"
          data-testid="admin-launch-rehearsal-refresh"
          onClick={refresh}
          disabled={loading}
          className="inline-flex h-9 items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60"
        >
          {loading ? <CircleNotch size={11} weight="bold" className="animate-spin" /> : <Confetti size={11} weight="duotone" />}
          {loading ? "Checking…" : "Refresh"}
        </button>
      </header>

      <ScoreRibbon score={score} loading={loading} />

      {personas.map((p) => (
        <PersonaSection key={p.key} persona={p} />
      ))}
    </div>
  );
}

function ScoreRibbon({ score, loading }) {
  const ready = score.ready;
  const total = score.total;
  const allLive = total > 0 && ready === total;
  return (
    <section
      data-testid="admin-launch-rehearsal-score"
      data-ready={ready}
      data-total={total}
      className={`rounded-2xl border p-4 ${
        allLive
          ? "border-primary/30 bg-primary/5"
          : "border-neutral-200 bg-white"
      }`}
    >
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-neutral-500">
            Live signals
          </p>
          <p
            data-testid="admin-launch-rehearsal-score-label"
            className="mt-1 font-heading text-base font-semibold tracking-tight text-neutral-900"
          >
            {loading
              ? "Checking…"
              : total === 0
                ? "Indicators initialising"
                : allLive
                  ? `All ${total} live signals confirmed — green light from the data.`
                  : `${ready} of ${total} live signals confirmed.`}
          </p>
        </div>
        <p className="text-[11px] text-neutral-500">
          Manual steps still need a visual walk-through.
        </p>
      </div>
    </section>
  );
}

function PersonaSection({ persona }) {
  const { key, label, icon: Icon, blurb, steps } = persona;
  return (
    <section
      data-testid={`admin-launch-rehearsal-persona-${key}`}
      className="rounded-2xl border border-neutral-200 bg-white p-5"
    >
      <header className="flex items-start gap-3">
        <span className="flex h-9 w-9 items-center justify-center rounded-2xl bg-primary/10 text-primary-hover">
          <Icon size={18} weight="duotone" />
        </span>
        <div>
          <h2 className="font-heading text-base font-semibold tracking-tight text-neutral-900">
            {label}
          </h2>
          <p className="text-[12px] text-neutral-500">{blurb}</p>
        </div>
      </header>
      <ol className="mt-4 space-y-2">
        {steps.map((s) => (
          <RehearsalStep key={s.key} personaKey={key} step={s} />
        ))}
      </ol>
    </section>
  );
}

function RehearsalStep({ personaKey, step }) {
  const ICON = {
    ok:      { Cmp: CheckCircle,  cls: "text-primary" },
    warn:    { Cmp: CheckCircle,  cls: "text-[#C6851E]" },
    pending: { Cmp: CircleDashed, cls: "text-neutral-400" },
    manual:  { Cmp: CircleDashed, cls: "text-neutral-400" },
  }[step.state] || { Cmp: CircleDashed, cls: "text-neutral-400" };
  const { Cmp, cls } = ICON;
  const statusLabel = {
    ok: "Confirmed",
    warn: "Review needed",
    pending: "Pending",
    manual: "Manual check",
  }[step.state] || "Pending";
  const ContainerEl = step.link ? Link : "div";
  const containerProps = step.link ? { to: step.link } : {};
  return (
    <li
      data-testid={`admin-launch-rehearsal-step-${personaKey}-${step.key}`}
      data-state={step.state}
      data-live={step.live ? "true" : "false"}
    >
      <ContainerEl
        {...containerProps}
        className={`flex items-start gap-3 rounded-2xl border border-neutral-100 bg-white p-3 ${
          step.link ? "transition-all hover:-translate-y-[1px] hover:border-primary/30 hover:bg-primary/5" : ""
        }`}
      >
        <Cmp
          size={18}
          weight="duotone"
          aria-hidden="true"
          className={`mt-0.5 flex-shrink-0 ${cls}`}
        />
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-semibold text-neutral-900">{step.label}</p>
          <p className="mt-0.5 text-[12px] leading-relaxed text-neutral-600">{step.detail}</p>
          <p
            data-testid={`admin-launch-rehearsal-step-${personaKey}-${step.key}-status`}
            role="status"
            className={`mt-1 text-[10px] font-bold uppercase tracking-wider ${step.state === "ok" ? "text-primary-hover" : "text-neutral-500"}`}
          >
            {statusLabel}{step.live ? " · live check" : ""}
          </p>
        </div>
        {step.link && (
          <ArrowRight size={12} weight="bold" className="mt-1 flex-shrink-0 text-neutral-400" />
        )}
      </ContainerEl>
    </li>
  );
}
